/**
 * CatalogReferencePicker — multi-select Combobox over a catalog leaf.
 *
 * Built 2026-05-11 to fix founder bugs C1+C2+C3 from the UI sweep:
 *
 *   • C1 — Sysadmin agent builder used a single text input for `modelBrand`
 *           instead of selecting from the model catalog.
 *   • C2 — Sysadmin agent composition (tools/functions/skills/workflows)
 *           used raw textareas where slugs had to be typed line-by-line.
 *   • C3 — Tenant agent builder used a hardcoded 3-item static dropdown
 *           for models.
 *
 * UI parity rule (founder feedback 2026-05-11): when the same entity
 * exists at both sysadmin and tenant scope, the UI must be identical.
 * This primitive is the canonical multi-select for ANY catalog leaf:
 *   models · functions · tools · skills · workflows · prompts · knowledge
 *
 * The picker is generic over the row shape — callers supply a `source`
 * that knows how to fetch the catalog and how to extract id / label /
 * description / status from each row. This means a single picker handles
 * argus models (with capabilities + runtime state) AND simple slug rows
 * (tools / functions / skills / workflows) without leaking each leaf's
 * column model into the picker itself.
 *
 * ── Features ────────────────────────────────────────────────────────────
 *   • Multi-select (default) or single-select (multiSelect={false}).
 *   • Async data source via a `useItems()` hook supplied by the caller.
 *   • Search-by-substring across label + description + id.
 *   • Selected items shown as removable badges above the search input.
 *   • Dropdown filters out already-selected items.
 *   • Per-row secondary description + optional status pill.
 *   • Empty / loading / error states using PR-0 canonical primitives
 *     (EmptyState, StatusPill).
 *   • Keyboard navigation (WAI-ARIA Combobox pattern):
 *       - ArrowDown / ArrowUp move the active option (with clamp at
 *         the ends; no wraparound — matches Radix Listbox semantics).
 *       - Enter selects the currently-active option.
 *       - Escape closes the dropdown without selecting.
 *       - Backspace on empty input removes the last selected item.
 *     The trigger button carries aria-controls + aria-haspopup +
 *     aria-activedescendant; each `<li role="option">` has a stable
 *     `id` so screen readers can announce the active row.
 *   • Maximum-selected enforcement (multiSelect only).
 *   • Disabled state surface for read-only displays.
 *
 * ── Tests ───────────────────────────────────────────────────────────────
 * The picker's filtering + selection-toggle + max-enforcement logic is
 * extracted into pure helpers (`_filterItems`, `_toggleSelection`,
 * `_canAddSelection`) so it can be unit-tested without rendering React.
 * See `__tests__/catalog-reference-picker.test.ts`.
 *
 * ── Example: sysadmin agent → models multi-select ──────────────────────
 *
 *   <CatalogReferencePicker
 *     leafKind="model"
 *     label="Models"
 *     description="Select one or more models the agent can run against."
 *     multiSelect={true}
 *     value={selectedModels}
 *     onChange={setSelectedModels}
 *     source={{
 *       useItems: () => {
 *         const q = useArgusModels();
 *         return { data: q.data?.items, isLoading: q.isLoading, error: q.error ?? undefined };
 *       },
 *       getId: (m) => m.id,
 *       getLabel: (m) => m.displayName,
 *       getDescription: (m) => m.brandId,
 *       getStatus: (m) => (m.isActive ? 'active' : 'inactive'),
 *     }}
 *   />
 */

import * as React from 'react';
import {
  ChevronsUpDown,
  X,
  Loader2,
  AlertTriangle,
  Search,
  Inbox,
  Check,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { StatusPill, type StatusPillStatus } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';

export type CatalogReferenceLeafKind =
  | 'model'
  | 'function'
  | 'tool'
  | 'skill'
  | 'workflow'
  | 'prompt'
  | 'knowledge';

export interface CatalogReferenceSource<TItem> {
  /** Async data source — returns the available items + loading/error state. */
  useItems: () => {
    data: TItem[] | undefined;
    isLoading: boolean;
    error?: Error;
  };
  /** How to extract a stable id from an item. The picker's `value` is an array of these ids. */
  getId: (item: TItem) => string;
  /** How to extract a display label. */
  getLabel: (item: TItem) => string;
  /** Optional secondary line (e.g., slug, brand, description, model task). */
  getDescription?: (item: TItem) => string | undefined;
  /** Optional status badge (e.g., 'draft', 'published', 'active'). */
  getStatus?: (item: TItem) => string | undefined;
}

export interface CatalogReferencePickerProps<TItem> {
  /** Which catalog leaf this picker is for (drives icon + empty-state copy). */
  leafKind: CatalogReferenceLeafKind;

  /** Label rendered above the picker. */
  label: string;

  /** Optional helper text rendered between label and picker. */
  description?: string;

  /** Multi-select. Default true. Set false for "primary model" or
   * single-prompt cases. When false, selecting a new item replaces the
   * existing selection (and `maxSelected` is ignored). */
  multiSelect?: boolean;

  /** Currently selected item ids (controlled). The picker is value-only;
   * it never reads metadata from selected items — full item rows are
   * looked up from `source.useItems()`. If a selected id is not present
   * in the source's data, it is rendered as a "missing" badge so the user
   * knows the reference is dangling. */
  value: string[];

  /** Called when selection changes. The picker calls onChange with the
   * full new array (not a diff). */
  onChange: (next: string[]) => void;

  /** Async data source — see CatalogReferenceSource. */
  source: CatalogReferenceSource<TItem>;

  /** Max selected items in multiSelect mode. Default: unlimited. */
  maxSelected?: number;

  /** Disabled state. Selected badges still render but cannot be removed. */
  disabled?: boolean;

  /** Optional dom id passed to the trigger button. */
  id?: string;

  /** Optional placeholder shown when nothing is selected and the picker
   * is closed. */
  placeholder?: string;

  /** Optional override of the empty-state body when no items match the
   * search. */
  emptyMessage?: string;

  /** Optional className applied to the outer wrapper. */
  className?: string;
}

// ─── Pure helpers (unit-testable without React) ────────────────────────────

/**
 * Case-insensitive substring filter against label + description + id.
 * Returns items in their original order.
 */
export function _filterItems<TItem>(
  items: readonly TItem[],
  search: string,
  source: Pick<CatalogReferenceSource<TItem>, 'getId' | 'getLabel' | 'getDescription'>,
): TItem[] {
  const needle = search.trim().toLowerCase();
  if (needle.length === 0) return items.slice();

  return items.filter((item) => {
    const label = source.getLabel(item).toLowerCase();
    const id = source.getId(item).toLowerCase();
    const description = source.getDescription?.(item)?.toLowerCase() ?? '';
    return (
      label.includes(needle) ||
      id.includes(needle) ||
      description.includes(needle)
    );
  });
}

/**
 * Toggle a single id in the selection. If `multiSelect` is false, the
 * new selection is exactly `[id]`. If `multiSelect` is true and the id
 * is already present, it is removed; otherwise it is appended. Order is
 * preserved (so the first model becomes the "primary" by convention).
 */
export function _toggleSelection(
  current: readonly string[],
  id: string,
  multiSelect: boolean,
): string[] {
  if (!multiSelect) {
    // Single-select: if already selected, deselect (empty); else replace.
    return current.length === 1 && current[0] === id ? [] : [id];
  }
  if (current.includes(id)) {
    return current.filter((x) => x !== id);
  }
  return [...current, id];
}

/**
 * Remove a single id from the selection. No-op if absent.
 */
export function _removeSelection(
  current: readonly string[],
  id: string,
): string[] {
  return current.filter((x) => x !== id);
}

/**
 * Enforce the maxSelected cap. Returns true if a new selection of the
 * given id would be permitted. Always true in single-select mode (the
 * pick replaces, not appends) or when the id is already selected (the
 * toggle would deselect, not add).
 */
export function _canAddSelection(
  current: readonly string[],
  id: string,
  multiSelect: boolean,
  maxSelected: number | undefined,
): boolean {
  if (!multiSelect) return true;
  if (current.includes(id)) return true;
  if (maxSelected === undefined) return true;
  return current.length < maxSelected;
}

/**
 * Compute the next active option index given a keyboard arrow direction.
 * Clamps at the ends (no wraparound) to match Radix Listbox semantics.
 *
 *   direction='down': next = min(current + 1, length - 1)
 *   direction='up':   next = max(current - 1, 0)
 *
 * When the filtered list is empty, returns -1 (no option active).
 * When `current` is -1 (no option active yet), arrow-down activates the
 * first row and arrow-up activates the last row — this matches typical
 * combobox UX where the first keystroke opens a "fresh" navigation.
 */
export function _clampActiveIndex(
  current: number,
  length: number,
  direction: 'up' | 'down',
): number {
  if (length <= 0) return -1;
  if (current < 0 || current >= length) {
    return direction === 'down' ? 0 : length - 1;
  }
  return direction === 'down'
    ? Math.min(current + 1, length - 1)
    : Math.max(current - 1, 0);
}

// ─── Status mapping ────────────────────────────────────────────────────────

/**
 * Map free-form status strings from catalog rows into the
 * StatusPillStatus palette. Unknown values fall through to 'neutral'
 * so we never throw on novel statuses.
 */
function statusToPill(s: string | undefined): StatusPillStatus {
  if (!s) return 'neutral';
  const t = s.toLowerCase();
  if (t === 'published' || t === 'active' || t === 'healthy') return 'healthy';
  if (t === 'draft' || t === 'pending' || t === 'warm') return 'warning';
  if (t === 'deprecated' || t === 'cold') return 'info';
  if (t === 'retired' || t === 'inactive' || t === 'disabled') return 'muted';
  if (t === 'failed' || t === 'destructive' || t === 'down') return 'destructive';
  return 'neutral';
}

const LEAF_LABEL: Record<CatalogReferenceLeafKind, string> = {
  model: 'models',
  function: 'functions',
  tool: 'tools',
  skill: 'skills',
  workflow: 'workflows',
  prompt: 'prompts',
  knowledge: 'knowledge items',
};

// ─── Component ─────────────────────────────────────────────────────────────

export function CatalogReferencePicker<TItem>({
  leafKind,
  label,
  description,
  multiSelect = true,
  value,
  onChange,
  source,
  maxSelected,
  disabled = false,
  id,
  placeholder,
  emptyMessage,
  className,
}: CatalogReferencePickerProps<TItem>) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  // Active option index for keyboard nav. -1 means "no row active";
  // arrow-down activates the first row, arrow-up activates the last.
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const listRef = React.useRef<HTMLUListElement | null>(null);
  // Stable id for aria-controls linking trigger ↔ listbox.
  const reactId = React.useId();
  const listboxId = id ? `${id}-listbox` : `picker-${reactId}-listbox`;
  const optionId = React.useCallback(
    (idx: number) => `${listboxId}-option-${idx}`,
    [listboxId],
  );

  const { data: items, isLoading, error } = source.useItems();

  // Index items by id once so the badge renderer can look up labels.
  const itemsById = React.useMemo(() => {
    const m = new Map<string, TItem>();
    (items ?? []).forEach((it) => m.set(source.getId(it), it));
    return m;
  }, [items, source]);

  // Visible rows = filtered by search, with already-selected items
  // greyed-out (but still visible so the user can re-click to deselect).
  const filtered = React.useMemo(
    () => _filterItems(items ?? [], search, source),
    [items, search, source],
  );

  // Reset active index whenever the search query or popover open state
  // changes — otherwise the active row could point past the end of the
  // newly-filtered list.
  React.useEffect(() => {
    setActiveIndex(filtered.length > 0 ? 0 : -1);
  }, [filtered.length, open]);

  // Scroll the active row into view on keyboard nav.
  React.useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const el = listRef.current.querySelector<HTMLElement>(
      `[data-option-index="${activeIndex}"]`,
    );
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function handleToggle(itemId: string): void {
    if (disabled) return;
    if (!_canAddSelection(value, itemId, multiSelect, maxSelected)) {
      // Cap reached and the item isn't currently selected — no-op.
      return;
    }
    const next = _toggleSelection(value, itemId, multiSelect);
    onChange(next);
    // In single-select mode, close the popover after a pick.
    if (!multiSelect) setOpen(false);
  }

  function handleRemove(itemId: string): void {
    if (disabled) return;
    onChange(_removeSelection(value, itemId));
  }

  function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    // Backspace on empty input removes the last selected item.
    if (
      e.key === 'Backspace' &&
      search.length === 0 &&
      value.length > 0 &&
      !disabled
    ) {
      onChange(value.slice(0, -1));
      return;
    }
    // WAI-ARIA Combobox keyboard navigation.
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((cur) => _clampActiveIndex(cur, filtered.length, 'down'));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((cur) => _clampActiveIndex(cur, filtered.length, 'up'));
      return;
    }
    if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < filtered.length) {
        e.preventDefault();
        const item = filtered[activeIndex];
        handleToggle(source.getId(item));
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  }

  const atCap =
    multiSelect &&
    maxSelected !== undefined &&
    value.length >= maxSelected;

  const triggerLabel = (() => {
    if (value.length === 0) {
      return placeholder ?? `Select ${LEAF_LABEL[leafKind]}…`;
    }
    if (!multiSelect) {
      const item = value[0] ? itemsById.get(value[0]) : undefined;
      return item ? source.getLabel(item) : value[0] ?? '';
    }
    return `${value.length} selected`;
  })();

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* Label + description */}
      <div className="flex items-start justify-between gap-2">
        <Label htmlFor={id} className="text-xs font-medium">
          {label}
          {multiSelect && maxSelected !== undefined && (
            <span className="ml-2 text-[10px] text-muted-foreground tabular-nums">
              {value.length}/{maxSelected}
            </span>
          )}
          {multiSelect && maxSelected === undefined && value.length > 0 && (
            <span className="ml-2 text-[10px] text-muted-foreground tabular-nums">
              {value.length} selected
            </span>
          )}
        </Label>
      </div>
      {description ? (
        <p className="text-[10px] text-muted-foreground leading-relaxed">
          {description}
        </p>
      ) : null}

      {/* Selected badges (multi-select mode only — single-select shows the
       *  label in the trigger button instead) */}
      {multiSelect && value.length > 0 ? (
        <div
          className="flex flex-wrap gap-1.5 rounded-md border border-input bg-background/50 p-1.5 min-h-[34px]"
          data-testid="catalog-picker-selected"
        >
          {value.map((selectedId) => {
            const item = itemsById.get(selectedId);
            const labelText = item ? source.getLabel(item) : selectedId;
            const isMissing = !item && !isLoading;
            return (
              <Badge
                key={selectedId}
                variant={isMissing ? 'outline' : 'secondary'}
                className={cn(
                  'text-[10px] font-medium gap-1 pl-2 pr-1 py-0.5',
                  isMissing &&
                    'border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300',
                )}
                title={
                  isMissing
                    ? `Missing reference: ${selectedId}`
                    : (source.getDescription?.(item!) ?? labelText)
                }
              >
                <span className="max-w-[18ch] truncate">
                  {labelText}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(selectedId)}
                    className="ml-0.5 rounded-sm hover:bg-foreground/10 p-0.5 -mr-0.5"
                    aria-label={`Remove ${labelText}`}
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      ) : null}

      {/* Trigger + dropdown */}
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-activedescendant={
              open && activeIndex >= 0 ? optionId(activeIndex) : undefined
            }
            disabled={disabled}
            className={cn(
              'w-full justify-between font-normal text-xs h-8',
              value.length === 0 && 'text-muted-foreground',
            )}
            title={atCap ? `Maximum ${maxSelected} selected — remove one to add another.` : undefined}
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="p-0 w-(--radix-popover-trigger-width) min-w-[280px]"
          align="start"
          sideOffset={4}
        >
          {/* Search input */}
          <div className="flex items-center border-b px-3 py-2">
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              type="text"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder={`Search ${LEAF_LABEL[leafKind]}…`}
              className="flex h-7 w-full rounded-md bg-transparent text-xs outline-none placeholder:text-muted-foreground"
            />
            {atCap && (
              <span className="ml-2 shrink-0 text-[10px] font-medium text-amber-700 dark:text-amber-400">
                Max reached
              </span>
            )}
          </div>

          {/* List body */}
          <div className="max-h-[300px] overflow-y-auto py-1">
            {isLoading ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading {LEAF_LABEL[leafKind]}…
              </div>
            ) : error ? (
              <div className="px-3 py-4 text-xs text-destructive flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium">Failed to load.</div>
                  <div className="text-[10px] opacity-80 mt-0.5">
                    {error.message}
                  </div>
                </div>
              </div>
            ) : filtered.length === 0 ? (
              <EmptyState
                icon={Inbox}
                compact
                title={
                  search.trim()
                    ? `No ${LEAF_LABEL[leafKind]} match "${search.trim()}"`
                    : `No ${LEAF_LABEL[leafKind]} available`
                }
                body={
                  emptyMessage ??
                  (search.trim()
                    ? 'Try a different search term.'
                    : `Create a ${leafKind} in the catalog first.`)
                }
              />
            ) : (
              <ul
                ref={listRef}
                role="listbox"
                id={listboxId}
                aria-multiselectable={multiSelect}
                className="space-y-0.5"
              >
                {filtered.map((item, idx) => {
                  const itemId = source.getId(item);
                  const selected = value.includes(itemId);
                  const itemLabel = source.getLabel(item);
                  const itemDescription = source.getDescription?.(item);
                  const itemStatus = source.getStatus?.(item);
                  const canAdd = _canAddSelection(value, itemId, multiSelect, maxSelected);
                  const isDisabledRow = !selected && !canAdd;
                  const isActive = idx === activeIndex;

                  return (
                    <li
                      key={itemId}
                      role="option"
                      id={optionId(idx)}
                      data-option-index={idx}
                      aria-selected={selected}
                    >
                      <button
                        type="button"
                        onClick={() => handleToggle(itemId)}
                        onMouseEnter={() => setActiveIndex(idx)}
                        disabled={isDisabledRow}
                        className={cn(
                          'flex w-full items-start gap-2 px-2 py-1.5 rounded-sm text-left',
                          'hover:bg-accent hover:text-accent-foreground',
                          'focus-visible:outline-none focus-visible:bg-accent focus-visible:text-accent-foreground',
                          selected && 'bg-primary/5',
                          isActive && 'bg-accent text-accent-foreground',
                          isDisabledRow && 'opacity-50 cursor-not-allowed hover:bg-transparent',
                        )}
                      >
                        <div
                          className={cn(
                            'mt-0.5 h-3.5 w-3.5 shrink-0 rounded-sm border flex items-center justify-center',
                            selected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input',
                          )}
                          aria-hidden
                        >
                          {selected ? <Check className="h-2.5 w-2.5" /> : null}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium truncate">
                              {itemLabel}
                            </span>
                            {itemStatus ? (
                              <StatusPill
                                status={statusToPill(itemStatus)}
                                compact
                                className="shrink-0"
                              >
                                {itemStatus}
                              </StatusPill>
                            ) : null}
                          </div>
                          {itemDescription ? (
                            <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                              {itemDescription}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
